import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Divider,
  Menu,
  MenuItem,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
  useTheme,
  Tooltip,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import SearchIcon from "@mui/icons-material/Search";
import ViewListIcon from "@mui/icons-material/ViewList";
import ViewModuleIcon from "@mui/icons-material/ViewModule";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import DownloadIcon from "@mui/icons-material/Download";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import CloseIcon from "@mui/icons-material/Close";
import ArrowBackIosNewIcon from "@mui/icons-material/ArrowBackIosNew";
import ArrowForwardIosIcon from "@mui/icons-material/ArrowForwardIos";
import FileCopyOutlinedIcon from "@mui/icons-material/FileCopyOutlined";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import Header from "../../components/Header";
import { tokens } from "../../theme";
import api from "../../api/client";
import { useAuth } from "../../context/AuthContext";
import { getRoleCapabilities } from "../../config/permissions";

/** Preview / upload URLs: same host as API, or relative (CRA dev proxy). */
function assetUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//i.test(path)) return path;
  const base = (api.defaults.baseURL || "")
    .replace(/\/$/, "")
    .replace(/\/api$/i, "");
  return base ? `${base}${path}` : path;
}

function editorBaseUrl() {
  const apiBase = (api.defaults.baseURL || "").replace(/\/$/, "");
  return apiBase.replace(/\/api$/i, "");
}

function fallbackPreviewBox() {
  return (
    <Box
      sx={{
        width: "100%",
        height: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
      }}
    >
      <Typography variant="caption" color="text.secondary" align="center" sx={{ px: 1 }}>
        No preview
      </Typography>
    </Box>
  );
}

function LayoutFallbackPreview({ row, variant = "card", zoom = 1 }) {
  const hint = row?.previewHint;
  if (!hint) return fallbackPreviewBox();

  if (hint.mode === "iframe" && hint.layoutKey) {
    const baseWidth = 1920;
    const baseHeight = 1080;
    const baseScale = variant === "modal" ? 0.58 : 0.2;
    const scale = Math.max(0.1, Math.min(2, baseScale * zoom));
    return (
      <Box sx={{ width: "100%", height: "100%", overflow: "hidden", bgcolor: "#fff", position: "relative" }}>
        <Box
          component="iframe"
          src={`/layouts/${encodeURIComponent(hint.layoutKey)}.html`}
          title={`layout-fallback-${row.id}`}
          scrolling="no"
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            width: baseWidth,
            height: baseHeight,
            border: 0,
            pointerEvents: "none",
            overflow: "hidden",
            transform: `translate(-50%, -50%) scale(${scale})`,
            transformOrigin: "center center",
            bgcolor: "#fff",
          }}
        />
      </Box>
    );
  }

  if (hint.mode === "canvas" && hint.canvasHTML) {
    return (
      <Box sx={{ width: "100%", height: "100%", overflow: "hidden", bgcolor: "#fff", position: "relative" }}>
        <Box
          sx={{
            position: "absolute",
            top: "50%",
            left: "50%",
            transform: "translate(-50%, -50%) scale(0.28)",
            transformOrigin: "center center",
            width: 1500,
            height: 1500,
            pointerEvents: "none",
            overflow: "hidden",
            "& .object": { position: "absolute" },
          }}
          dangerouslySetInnerHTML={{ __html: hint.canvasHTML }}
        />
      </Box>
    );
  }

  return fallbackPreviewBox();
}

function LayoutPreviewSurface({ row, zoom = 1, variant = "card" }) {
  const displayPreview = row.previewUrl || row.generatedPreviewUrl;
  if (displayPreview) {
    return (
      <Box
        component="img"
        src={assetUrl(displayPreview)}
        alt={row.title || "Layout preview"}
        loading="lazy"
        sx={{
          width: "100%",
          height: "100%",
          objectFit: "contain",
          display: "block",
          transform: `scale(${zoom})`,
          transformOrigin: "center center",
          transition: "transform 180ms ease",
        }}
      />
    );
  }
  return <LayoutFallbackPreview row={row} variant={variant} zoom={zoom} />;
}

const VIEW_LIST = "list";
const VIEW_DETAILS = "details";
const VIEW_ICONS = "icons";

function formatSavedAt(value) {
  if (!value) return "—";
  try {
    return new Date(value).toLocaleString(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
    });
  } catch {
    return "—";
  }
}

function safeBaseName(row) {
  const raw = (row.title || row.fileName || "layout").replace(/\.json$/i, "");
  const cleaned = raw.replace(/[/\\?%*:|"<>]/g, "-").trim().slice(0, 120);
  return cleaned || "layout";
}

function triggerBlobDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  a.style.display = "none";
  document.body.appendChild(a);
  a.click();
  setTimeout(() => {
    a.remove();
    URL.revokeObjectURL(url);
  }, 2500);
}

function blobToCanvas(blob) {
  return new Promise((resolve, reject) => {
    const objUrl = URL.createObjectURL(blob);
    const img = new Image();
    img.onload = () => {
      try {
        const c = document.createElement("canvas");
        c.width = img.naturalWidth || 1;
        c.height = img.naturalHeight || 1;
        const ctx = c.getContext("2d");
        if (!ctx) throw new Error("Canvas not available");
        ctx.drawImage(img, 0, 0);
        resolve(c);
      } catch (e) {
        reject(e);
      } finally {
        URL.revokeObjectURL(objUrl);
      }
    };
    img.onerror = () => {
      URL.revokeObjectURL(objUrl);
      reject(new Error("Could not decode preview image"));
    };
    img.src = objUrl;
  });
}

async function fetchPreviewBlob(previewUrl) {
  const { data, headers } = await api.get(previewUrl, { responseType: "blob" });
  const ct = (headers && headers["content-type"]) || "";
  if (typeof ct === "string" && ct.includes("application/json")) {
    const text = await data.text();
    let msg = "Preview request failed";
    try {
      const j = JSON.parse(text);
      if (j.message) msg = j.message;
    } catch {
      /* ignore */
    }
    throw new Error(msg);
  }
  return data;
}

function placeholderCanvas(row) {
  const canvas = document.createElement("canvas");
  canvas.width = 1200;
  canvas.height = 1200;
  const ctx = canvas.getContext("2d");
  if (!ctx) return canvas;
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.fillStyle = "#253354";
  ctx.font = "bold 56px Arial, sans-serif";
  ctx.textAlign = "center";
  ctx.fillText(row?.title || "Layout", canvas.width / 2, canvas.height / 2 - 10);
  ctx.fillStyle = "#6b7280";
  ctx.font = "32px Arial, sans-serif";
  ctx.fillText("Generated preview", canvas.width / 2, canvas.height / 2 + 46);
  return canvas;
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("Image encode failed"))), type, quality);
  });
}

function normalizeCanvasToSquare(sourceCanvas, size = 1200) {
  const srcW = sourceCanvas.width || 1;
  const srcH = sourceCanvas.height || 1;
  const srcCtx = sourceCanvas.getContext("2d");
  if (!srcCtx) return sourceCanvas;

  const { data } = srcCtx.getImageData(0, 0, srcW, srcH);
  let minX = srcW;
  let minY = srcH;
  let maxX = -1;
  let maxY = -1;

  // Detect non-near-white pixels to find actual content bounds.
  for (let y = 0; y < srcH; y += 1) {
    for (let x = 0; x < srcW; x += 1) {
      const i = (y * srcW + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const a = data[i + 3];
      const isContent = a > 10 && (r < 245 || g < 245 || b < 245);
      if (!isContent) continue;
      if (x < minX) minX = x;
      if (y < minY) minY = y;
      if (x > maxX) maxX = x;
      if (y > maxY) maxY = y;
    }
  }

  const out = document.createElement("canvas");
  out.width = size;
  out.height = size;
  const outCtx = out.getContext("2d");
  if (!outCtx) return sourceCanvas;
  outCtx.fillStyle = "#ffffff";
  outCtx.fillRect(0, 0, size, size);

  if (maxX < minX || maxY < minY) {
    // No detected content; fallback to fitting full source canvas.
    const fullScale = Math.min(size / srcW, size / srcH);
    const fw = srcW * fullScale;
    const fh = srcH * fullScale;
    outCtx.drawImage(sourceCanvas, (size - fw) / 2, (size - fh) / 2, fw, fh);
    return out;
  }

  const pad = 24;
  const cropX = Math.max(0, minX - pad);
  const cropY = Math.max(0, minY - pad);
  const cropW = Math.min(srcW - cropX, maxX - minX + 1 + pad * 2);
  const cropH = Math.min(srcH - cropY, maxY - minY + 1 + pad * 2);

  const scale = Math.min((size * 0.92) / cropW, (size * 0.92) / cropH);
  const drawW = cropW * scale;
  const drawH = cropH * scale;
  const dx = (size - drawW) / 2;
  const dy = (size - drawH) / 2;

  outCtx.drawImage(sourceCanvas, cropX, cropY, cropW, cropH, dx, dy, drawW, drawH);
  return out;
}

async function renderCanvasHintToCanvas(hint) {
  if (!hint?.canvasHTML) return null;
  const host = document.createElement("div");
  host.style.position = "fixed";
  host.style.left = "-100000px";
  host.style.top = "0";
  host.style.width = "1500px";
  host.style.height = "1500px";
  host.style.overflow = "hidden";
  host.style.background = "#fff";
  host.innerHTML = `<div style="position:relative;width:1500px;height:1500px;background:#fff;">${hint.canvasHTML}</div>`;
  document.body.appendChild(host);
  try {
    const rawCanvas = await html2canvas(host, {
      width: 1500,
      height: 1500,
      scale: 1,
      backgroundColor: "#ffffff",
      useCORS: true,
      logging: false,
    });
    return normalizeCanvasToSquare(rawCanvas, 1200);
  } finally {
    host.remove();
  }
}

async function resolveRowPreviewCanvas(row) {
  if (row.previewUrl) {
    const blob = await fetchPreviewBlob(row.previewUrl);
    return blobToCanvas(blob);
  }
  if (row.previewHint?.mode === "canvas" && row.previewHint?.canvasHTML) {
    const canvas = await renderCanvasHintToCanvas(row.previewHint);
    if (canvas) return canvas;
  }
  return placeholderCanvas(row);
}

async function downloadPreviewJpeg(row) {
  const canvas = await resolveRowPreviewCanvas(row);
  const out = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("JPEG encode failed"))), "image/jpeg", 0.92);
  });
  triggerBlobDownload(out, `${safeBaseName(row)}.jpg`);
}

async function downloadPreviewPng(row) {
  const canvas = await resolveRowPreviewCanvas(row);
  const out = await new Promise((resolve, reject) => {
    canvas.toBlob((b) => (b ? resolve(b) : reject(new Error("PNG encode failed"))), "image/png");
  });
  triggerBlobDownload(out, `${safeBaseName(row)}.png`);
}

async function downloadPreviewPdf(row) {
  const canvas = await resolveRowPreviewCanvas(row);
  const w = canvas.width;
  const h = canvas.height;
  const pdf = new jsPDF({
    unit: "px",
    format: [w, h],
    orientation: w >= h ? "landscape" : "portrait",
    compress: true,
  });
  const png = canvas.toDataURL("image/png");
  pdf.addImage(png, "PNG", 0, 0, w, h);
  pdf.save(`${safeBaseName(row)}.pdf`);
}

function DownloadLayoutMenu({ row, disabled }) {
  const [anchor, setAnchor] = useState(null);
  const open = Boolean(anchor);
  const busyRef = useRef(false);

  const run = async (fn) => {
    if (busyRef.current) return;
    busyRef.current = true;
    setAnchor(null);
    try {
      await fn(row);
    } catch (e) {
      console.error(e);
      window.alert(e?.message || "Download failed");
    } finally {
      busyRef.current = false;
    }
  };

  return (
    <>
      <IconButton
        size="small"
        aria-label="Download layout"
        disabled={disabled}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setAnchor(e.currentTarget);
        }}
      >
        <DownloadIcon fontSize="small" />
      </IconButton>
      <Menu
        anchorEl={anchor}
        open={open}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        slotProps={{ paper: { sx: { minWidth: 220 } } }}
      >
        <Box sx={{ px: 2, py: 1, maxWidth: 300 }}>
          <Typography variant="caption" color="text.secondary" display="block">
            Files go to your browser’s Downloads folder.
          </Typography>
        </Box>
        <Divider />
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            run(downloadPreviewJpeg);
          }}
        >
          JPG
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            run(downloadPreviewPng);
          }}
        >
          PNG
        </MenuItem>
        <MenuItem
          onClick={(e) => {
            e.stopPropagation();
            run(downloadPreviewPdf);
          }}
        >
          PDF
        </MenuItem>
      </Menu>
    </>
  );
}

export default function BrandLayouts() {
  const { brandId } = useParams();
  const { user } = useAuth();
  const caps = useMemo(() => getRoleCapabilities(user?.role), [user?.role]);
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("updatedAt");
  const [loading, setLoading] = useState(false);
  const [view, setView] = useState(VIEW_ICONS);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [previewZoom, setPreviewZoom] = useState(1);
  const [visibleCount, setVisibleCount] = useState(12);
  const [generatedPreviewById, setGeneratedPreviewById] = useState({});
  const qRef = useRef(q);
  const generatingRef = useRef(new Set());
  qRef.current = q;

  const openLayoutEditor = useCallback(
    (layoutId) => {
      const params = new URLSearchParams();
      params.set("from", "dashboard");
      params.set("brandId", brandId);
      params.set("apiBase", editorBaseUrl());
      if (layoutId) {
        // Support both modern and legacy auto-load query keys.
        params.set("loadBrandLayoutId", layoutId);
        params.set("savedLayoutId", layoutId);
      }
      window.location.href = `/layoutEditor.html?${params.toString()}`;
    },
    [brandId]
  );

  const derivePreviewHint = useCallback((document) => {
    const pageRecords = Array.isArray(document?.pageRecords) ? document.pageRecords : [];
    if (!pageRecords.length) return null;
    const idxRaw = Number.isFinite(Number(document?.currentPageIndex)) ? Number(document.currentPageIndex) : 0;
    const idx = Math.min(Math.max(0, idxRaw), Math.max(0, pageRecords.length - 1));
    const page = pageRecords[idx];
    if (!page || typeof page !== "object") return null;
    return {
      mode: page.mode === "iframe" ? "iframe" : "canvas",
      layoutKey: typeof page.layoutKey === "string" ? page.layoutKey : "",
      canvasHTML: typeof page.canvasHTML === "string" ? page.canvasHTML.slice(0, 80000) : "",
    };
  }, []);

  const load = useCallback(
    async (searchText) => {
      const qParam = searchText !== undefined && searchText !== null ? searchText : qRef.current;
      setLoading(true);
      try {
        const { data } = await api.get(`/api/brands/${brandId}/layouts`, {
          params: { q: qParam, sort },
        });
        const mapped = (data || []).map((r) => ({ ...r, id: r.id }));
        const needsHint = mapped.filter((r) => !r.previewUrl && !r.previewHint).slice(0, 24);
        if (needsHint.length) {
          const hydrated = await Promise.all(
            needsHint.map(async (row) => {
              try {
                const { data: detail } = await api.get(`/api/brands/${brandId}/layouts/${row.id}`);
                return { id: row.id, previewHint: derivePreviewHint(detail?.document) };
              } catch {
                return { id: row.id, previewHint: null };
              }
            })
          );
          const hintMap = new Map(hydrated.map((h) => [h.id, h.previewHint]));
          setRows(mapped.map((r) => (hintMap.has(r.id) ? { ...r, previewHint: hintMap.get(r.id) } : r)));
        } else {
          setRows(mapped);
        }
        setVisibleCount(12);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    },
    [brandId, sort, derivePreviewHint]
  );

  useEffect(() => {
    load("");
  }, [brandId, sort, load]);

  useEffect(() => {
    let cancelled = false;
    const candidates = rows
      .filter((row) => !row.previewUrl && !generatedPreviewById[row.id] && row.previewHint?.mode === "canvas" && row.previewHint?.canvasHTML)
      .slice(0, 24);
    if (!candidates.length) return;
    (async () => {
      for (const row of candidates) {
        if (cancelled) break;
        if (generatingRef.current.has(row.id)) continue;
        generatingRef.current.add(row.id);
        try {
          const canvas = await renderCanvasHintToCanvas(row.previewHint);
          if (!canvas || cancelled) continue;
          const blob = await canvasToBlob(canvas, "image/png");
          const objectUrl = URL.createObjectURL(blob);
          if (cancelled) {
            URL.revokeObjectURL(objectUrl);
            continue;
          }
          setGeneratedPreviewById((prev) => {
            const oldUrl = prev[row.id];
            if (oldUrl && oldUrl !== objectUrl) URL.revokeObjectURL(oldUrl);
            return { ...prev, [row.id]: objectUrl };
          });
        } catch {
          /* ignore generation errors for list tiles */
        } finally {
          generatingRef.current.delete(row.id);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [rows, generatedPreviewById]);

  useEffect(
    () => () => {
      Object.values(generatedPreviewById).forEach((url) => URL.revokeObjectURL(url));
    },
    [generatedPreviewById]
  );

  const openPreview = useCallback(
    (id) => {
      const idx = rows.findIndex((r) => r.id === id);
      if (idx < 0) return;
      setActiveIndex(idx);
      setPreviewZoom(1);
      setPreviewOpen(true);
    },
    [rows]
  );

  const duplicateLayout = useCallback(
    async (id) => {
      await api.post(`/api/brands/${brandId}/layouts/${id}/duplicate`);
      await load(qRef.current);
    },
    [brandId, load]
  );

  const deleteLayout = useCallback(
    async (id) => {
      if (!window.confirm("Delete this layout? This cannot be undone.")) return;
      await api.delete(`/api/brands/${brandId}/layouts/${id}`);
      await load(qRef.current);
    },
    [brandId, load]
  );

  const openFullscreen = useCallback(() => {
    const el = document.getElementById("layout-preview-modal-content");
    if (!el) return;
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => {});
      return;
    }
    el.requestFullscreen?.().catch(() => {});
  }, []);

  const rowsWithGeneratedPreview = rows.map((row) => ({
    ...row,
    generatedPreviewUrl: generatedPreviewById[row.id] || "",
  }));
  const activeRow = rowsWithGeneratedPreview[activeIndex] || null;
  const visibleRows = view === VIEW_ICONS ? rowsWithGeneratedPreview.slice(0, visibleCount) : rowsWithGeneratedPreview;

  const columns = [
    {
      field: "previewUrl",
      headerName: "Preview",
      width: 100,
      renderCell: (p) =>
        p.value ? (
          <Box
            component="img"
            src={assetUrl(p.value)}
            alt=""
            sx={{ width: 72, height: 48, objectFit: "cover", borderRadius: 1 }}
          />
        ) : (
          <Box sx={{ width: 72, height: 48, borderRadius: 1, bgcolor: "action.hover", overflow: "hidden" }}>
            <LayoutFallbackPreview row={p.row} />
          </Box>
        ),
    },
    { field: "title", headerName: "Title", flex: 1, minWidth: 140 },
    { field: "brandName", headerName: "Brand", minWidth: 160 },
    { field: "designerName", headerName: "Designer", minWidth: 180 },
    { field: "fileName", headerName: "File", flex: 1, minWidth: 160 },
    {
      field: "updatedAt",
      headerName: "Last saved",
      minWidth: 180,
      valueFormatter: (p) => formatSavedAt(p.value),
    },
    {
      field: "createdAt",
      headerName: "Created",
      minWidth: 160,
      valueFormatter: (p) => formatSavedAt(p.value),
    },
    {
      field: "actions",
      headerName: "Actions",
      width: 300,
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box display="flex" gap={0.5} alignItems="center" flexWrap="wrap">
          <Button size="small" onClick={() => openPreview(params.row.id)}>
            Open
          </Button>
          <Button size="small" onClick={() => openLayoutEditor(params.row.id)}>
            Edit
          </Button>
          <Button
            size="small"
            onClick={() => duplicateLayout(params.row.id)}
          >
            Duplicate
          </Button>
          {caps.canDeleteLayouts ? (
            <Button size="small" color="error" onClick={() => deleteLayout(params.row.id)}>
              Delete
            </Button>
          ) : null}
          <DownloadLayoutMenu row={params.row} />
        </Box>
      ),
    },
  ];

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Header title="LAYOUTS" subtitle="Layouts for this brand" />
        <Button variant="contained" color="secondary" onClick={() => openLayoutEditor()}>
          Create New Layout
        </Button>
      </Box>
      <Box display="flex" gap={2} mt={2} mb={1} flexWrap="wrap" alignItems="center">
        <TextField
          size="small"
          placeholder="Search layouts"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && load(q)}
          sx={{ minWidth: 260 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
        <TextField select size="small" label="Sort" value={sort} onChange={(e) => setSort(e.target.value)} sx={{ minWidth: 160 }}>
          <MenuItem value="updatedAt">Updated (newest)</MenuItem>
          <MenuItem value="createdAt">Created (newest)</MenuItem>
          <MenuItem value="title">Title (A–Z)</MenuItem>
          <MenuItem value="fileName">File name (A–Z)</MenuItem>
        </TextField>
        <Button variant="outlined" onClick={() => load(q)}>
          Apply
        </Button>
      </Box>
      <Box display="flex" flexWrap="wrap" alignItems="center" gap={1} mb={2}>
        <Typography variant="body2" color="text.secondary" sx={{ mr: 1 }}>
          View
        </Typography>
        <ToggleButtonGroup
          size="small"
          value={view}
          exclusive
          onChange={(_, v) => v && setView(v)}
          aria-label="Layout view mode"
          color="secondary"
        >
          <ToggleButton value={VIEW_LIST}>
            <ViewListIcon fontSize="small" sx={{ mr: 0.5 }} />
            List
          </ToggleButton>
          <ToggleButton value={VIEW_DETAILS}>
            <InfoOutlinedIcon fontSize="small" sx={{ mr: 0.5 }} />
            Details
          </ToggleButton>
          <ToggleButton value={VIEW_ICONS}>
            <ViewModuleIcon fontSize="small" sx={{ mr: 0.5 }} />
            Large icons
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {view === VIEW_LIST && (
        <Box height="65vh" sx={gridSx(colors)}>
          <DataGrid rows={rows} columns={columns} loading={loading} />
        </Box>
      )}

      {view === VIEW_DETAILS && (
        <Box sx={{ borderRadius: 1, overflow: "hidden", bgcolor: colors.primary[400], border: `1px solid ${colors.grey[100]}` }}>
          <List dense={false} sx={{ py: 0 }}>
            {rows.length === 0 && !loading ? (
              <ListItem>
                <ListItemText primary="No layouts" secondary="Create one or adjust search." />
              </ListItem>
            ) : (
              rows.map((row) => (
                <ListItem
                  key={row.id}
                  alignItems="flex-start"
                  divider
                  secondaryAction={
                    <Box display="flex" gap={0.5} alignItems="center" flexWrap="wrap" sx={{ mt: 1 }}>
                      <Button size="small" onClick={() => openPreview(row.id)}>
                        Open
                      </Button>
                      <Button size="small" onClick={() => openLayoutEditor(row.id)}>
                        Edit
                      </Button>
                      <Button size="small" onClick={() => duplicateLayout(row.id)}>
                        Duplicate
                      </Button>
                      {caps.canDeleteLayouts ? (
                        <Button size="small" color="error" onClick={() => deleteLayout(row.id)}>
                          Delete
                        </Button>
                      ) : null}
                      <DownloadLayoutMenu row={row} />
                    </Box>
                  }
                >
                  <ListItemAvatar sx={{ minWidth: 104, mr: 2, alignSelf: "flex-start" }}>
                    {row.previewUrl ? (
                      <Box
                        component="img"
                        src={assetUrl(row.previewUrl)}
                        alt=""
                        sx={{
                          width: 96,
                          height: 96,
                          objectFit: "contain",
                          borderRadius: 1,
                          bgcolor: "action.hover",
                        }}
                      />
                    ) : (
                      <Box sx={{ width: 96, height: 96, borderRadius: 1, bgcolor: "action.hover", overflow: "hidden" }}>
                        <LayoutFallbackPreview row={row} />
                      </Box>
                    )}
                  </ListItemAvatar>
                  <ListItemText
                    primary={
                      <Typography variant="subtitle1" fontWeight={600}>
                        {row.title || "Untitled"}
                      </Typography>
                    }
                    secondary={
                      <Box component="span" display="block" sx={{ mt: 0.5 }}>
                        <Typography component="span" variant="body2" color="text.secondary" display="block">
                          File: {row.fileName || "—"}
                        </Typography>
                        <Typography component="span" variant="body2" color="text.secondary" display="block">
                          Brand: {row.brandName || "—"}
                        </Typography>
                        <Typography component="span" variant="body2" color="text.secondary" display="block">
                          Designer: {row.designerName || "—"}
                        </Typography>
                        <Typography component="span" variant="body2" color="text.secondary" display="block">
                          Last saved: {formatSavedAt(row.updatedAt)}
                        </Typography>
                        <Typography component="span" variant="body2" color="text.secondary" display="block">
                          Created: {formatSavedAt(row.createdAt)}
                        </Typography>
                      </Box>
                    }
                  />
                </ListItem>
              ))
            )}
          </List>
        </Box>
      )}

      {view === VIEW_ICONS && (
        <>
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, minmax(0, 1fr))",
                lg: "repeat(3, minmax(0, 1fr))",
                xl: "repeat(4, minmax(0, 1fr))",
              },
              gap: 2.5,
            }}
          >
            {visibleRows.map((row) => (
              <Card
                key={row.id}
                sx={{
                  bgcolor: colors.primary[400],
                  borderRadius: 3,
                  overflow: "hidden",
                  border: `1px solid ${colors.grey[100]}22`,
                  boxShadow: "0 12px 28px rgba(0,0,0,0.25)",
                  transition: "transform 220ms ease, box-shadow 220ms ease",
                  "&:hover": {
                    transform: "translateY(-4px)",
                    boxShadow: "0 18px 40px rgba(0,0,0,0.35)",
                    "& .layout-preview-media": { transform: "scale(1.04)" },
                    "& .layout-preview-overlay": { opacity: 1 },
                  },
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    width: "100%",
                    aspectRatio: "1 / 1",
                    bgcolor: theme.palette.mode === "dark" ? "grey.900" : "grey.100",
                    overflow: "hidden",
                    cursor: "pointer",
                  }}
                  onClick={() => openPreview(row.id)}
                >
                  <Box className="layout-preview-media" sx={{ width: "100%", height: "100%", transition: "transform 240ms ease" }}>
                    <LayoutPreviewSurface row={row} variant="card" />
                  </Box>
                  <Box
                    className="layout-preview-overlay"
                    sx={{
                      position: "absolute",
                      inset: 0,
                      background: "linear-gradient(180deg, rgba(0,0,0,0.05) 20%, rgba(0,0,0,0.75) 100%)",
                      opacity: 0,
                      transition: "opacity 200ms ease",
                      p: 1.25,
                      display: "flex",
                      alignItems: "flex-end",
                    }}
                  >
                    <Box display="flex" gap={0.75} flexWrap="wrap">
                      <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); openPreview(row.id); }}>
                        Open
                      </Button>
                      <Button size="small" variant="contained" onClick={(e) => { e.stopPropagation(); openLayoutEditor(row.id); }}>
                        Edit
                      </Button>
                      <IconButton size="small" sx={{ bgcolor: "rgba(255,255,255,0.15)" }} onClick={(e) => { e.stopPropagation(); duplicateLayout(row.id); }}>
                        <FileCopyOutlinedIcon fontSize="small" />
                      </IconButton>
                      {caps.canDeleteLayouts ? (
                        <IconButton size="small" sx={{ bgcolor: "rgba(255,255,255,0.15)" }} onClick={(e) => { e.stopPropagation(); deleteLayout(row.id); }}>
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      ) : null}
                      <DownloadLayoutMenu row={row} />
                    </Box>
                  </Box>
                </Box>
                <CardContent sx={{ py: 1.5 }}>
                  <Typography variant="subtitle1" fontWeight={700} noWrap title={row.title || "Untitled"}>
                    {row.title || "Untitled"}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" noWrap>
                    {row.brandName || "Brand"} · {row.designerName || "Designer"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" display="block">
                    Updated {formatSavedAt(row.updatedAt)}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
          {rows.length === 0 && !loading ? (
            <Typography color="text.secondary" mt={2}>
              No layouts
            </Typography>
          ) : null}
          {visibleCount < rows.length ? (
            <Box display="flex" justifyContent="center" mt={2}>
              <Button variant="outlined" onClick={() => setVisibleCount((n) => n + 12)}>
                Load more
              </Button>
            </Box>
          ) : null}
        </>
      )}
      <Dialog open={previewOpen && Boolean(activeRow)} onClose={() => setPreviewOpen(false)} fullWidth maxWidth="lg">
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 1 }}>
          <Box>
            <Typography variant="h6">{activeRow?.title || "Layout preview"}</Typography>
            <Typography variant="caption" color="text.secondary">
              {activeRow?.brandName || "Brand"} · {activeRow?.designerName || "Designer"} · {formatSavedAt(activeRow?.updatedAt)}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={0.5}>
            <Tooltip title="Zoom out">
              <span>
                <IconButton onClick={() => setPreviewZoom((z) => Math.max(0.5, Number((z - 0.1).toFixed(2))))}>
                  <ZoomOutIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Zoom in">
              <span>
                <IconButton onClick={() => setPreviewZoom((z) => Math.min(2, Number((z + 0.1).toFixed(2))))}>
                  <ZoomInIcon fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Tooltip title="Fullscreen">
              <IconButton onClick={openFullscreen}>
                <OpenInFullIcon fontSize="small" />
              </IconButton>
            </Tooltip>
            <IconButton onClick={() => setPreviewOpen(false)}>
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers id="layout-preview-modal-content">
          <Box sx={{ position: "relative", height: { xs: 360, md: 640 }, bgcolor: theme.palette.mode === "dark" ? "grey.900" : "grey.100", borderRadius: 2, overflow: "hidden" }}>
            <Box sx={{ width: "100%", height: "100%" }}>
              {activeRow ? <LayoutPreviewSurface row={activeRow} zoom={previewZoom} variant="modal" /> : null}
            </Box>
            <IconButton
              onClick={() => setActiveIndex((i) => (i <= 0 ? rows.length - 1 : i - 1))}
              sx={{ position: "absolute", top: "50%", left: 12, transform: "translateY(-50%)", bgcolor: "rgba(0,0,0,0.45)" }}
            >
              <ArrowBackIosNewIcon fontSize="small" />
            </IconButton>
            <IconButton
              onClick={() => setActiveIndex((i) => (i >= rows.length - 1 ? 0 : i + 1))}
              sx={{ position: "absolute", top: "50%", right: 12, transform: "translateY(-50%)", bgcolor: "rgba(0,0,0,0.45)" }}
            >
              <ArrowForwardIosIcon fontSize="small" />
            </IconButton>
          </Box>
          <Box mt={2} display="flex" gap={1} flexWrap="wrap">
            <Button startIcon={<EditOutlinedIcon />} variant="contained" onClick={() => activeRow && openLayoutEditor(activeRow.id)}>
              Edit
            </Button>
            <Button startIcon={<FileCopyOutlinedIcon />} variant="outlined" onClick={() => activeRow && duplicateLayout(activeRow.id)}>
              Duplicate
            </Button>
            {caps.canDeleteLayouts ? (
              <Button startIcon={<DeleteOutlineIcon />} color="error" variant="outlined" onClick={() => activeRow && deleteLayout(activeRow.id)}>
                Delete
              </Button>
            ) : null}
            {activeRow ? <DownloadLayoutMenu row={activeRow} /> : null}
          </Box>
        </DialogContent>
      </Dialog>
    </Box>
  );
}

function gridSx(colors) {
  return {
    "& .MuiDataGrid-root": { border: "none" },
    "& .MuiDataGrid-cell": { borderBottom: "none" },
    "& .MuiDataGrid-columnHeaders": {
      backgroundColor: colors.blueAccent[700],
      borderBottom: "none",
    },
    "& .MuiDataGrid-virtualScroller": { backgroundColor: colors.primary[400] },
    "& .MuiDataGrid-footerContainer": {
      borderTop: "none",
      backgroundColor: colors.blueAccent[700],
    },
  };
}
